import { useCallback, useEffect, useRef, useState } from "react";
import { ErreurApi } from "@/lib/http";

/**
 * Charger, afficher, réessayer — écrit une fois plutôt que sur chaque écran.
 *
 * Le plan du LOT 0 impose « gestion des erreurs et des états de chargement » ;
 * recopier ce triplet dans dix écrans le ferait diverger dès le onzième. Le
 * `AbortController` n'est pas un luxe : sans lui, quitter un écran pendant son
 * chargement écrit dans un composant démonté, et l'erreur qui en résulte n'a
 * aucun rapport avec sa cause.
 */
export type Chargeable<T> = {
  donnees: T | null;
  erreur: string | null;
  enCours: boolean;
  recharger: () => void;
};

export function useChargement<T>(
  requete: (signal: AbortSignal) => Promise<T>,
  dependances: unknown[],
): Chargeable<T> {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(true);
  const [tentative, setTentative] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appel = useCallback(requete, dependances);

  useEffect(() => {
    const controleur = new AbortController();
    setEnCours(true);
    setErreur(null);
    appel(controleur.signal)
      .then((resultat) => {
        setDonnees(resultat);
        setEnCours(false);
      })
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setErreur(cause instanceof ErreurApi ? cause.message : "Une erreur est survenue.");
        setEnCours(false);
      });
    return () => controleur.abort();
  }, [appel, tentative]);

  const recharger = useCallback(() => setTentative((n) => n + 1), []);
  return { donnees, erreur, enCours, recharger };
}

/** Le message d'une erreur d'API, quelle que soit sa cause. */
export function messageDe(cause: unknown) {
  return cause instanceof ErreurApi ? cause.message : "Une erreur est survenue.";
}

/**
 * Une liste qui se charge par pages, et qui sait combien il en reste.
 *
 * Écrit une fois pour les trois listes du LOT 1, et pour celles des lots
 * suivants. Deux règles le gouvernent :
 *
 *  * **la première page REMPLACE, les suivantes AJOUTENT**. Charger la suite est
 *    un geste de l'utilisateur, jamais un effet : c'est ce qui met l'accumulation
 *    à l'abri du double montage de `StrictMode`, qui dédoublerait autrement
 *    chaque page en développement — et seulement en développement, donc
 *    invisible jusqu'à la démonstration ;
 *  * **un changement de filtre repart de zéro**. Garder les anciens éléments
 *    ferait cohabiter deux recherches dans la même liste.
 */
export type ListePaginee<T> = {
  elements: T[];
  total: number;
  erreur: string | null;
  enCours: boolean;
  chargeLaSuite: boolean;
  reste: number;
  chargerLaSuite: () => void;
  recharger: () => void;
};

export function useListePaginee<T>(
  charger: (decalage: number, signal: AbortSignal) => Promise<{
    elements: T[];
    total: number;
    decalage: number;
  }>,
  dependances: unknown[],
): ListePaginee<T> {
  const [elements, setElements] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(true);
  const [chargeLaSuite, setChargeLaSuite] = useState(false);
  const [tentative, setTentative] = useState(0);
  const vivant = useRef(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appel = useCallback(charger, dependances);

  useEffect(() => {
    const controleur = new AbortController();
    setEnCours(true);
    setErreur(null);
    appel(0, controleur.signal)
      .then((page) => {
        setElements(page.elements);
        setTotal(page.total);
        setEnCours(false);
      })
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setErreur(messageDe(cause));
        setEnCours(false);
      });
    return () => controleur.abort();
  }, [appel, tentative]);

  useEffect(() => {
    vivant.current = true;
    return () => {
      vivant.current = false;
    };
  }, []);

  const chargerLaSuite = useCallback(() => {
    setChargeLaSuite(true);
    setErreur(null);
    // Le décalage vient de ce qui est DÉJÀ affiché, et non d'un compteur de
    // pages : si une page revient plus courte que prévu, la suivante reprend
    // quand même au bon endroit.
    appel(elements.length, new AbortController().signal)
      .then((page) => {
        if (!vivant.current) return;
        setElements((anciens) => [...anciens, ...page.elements]);
        setTotal(page.total);
        setChargeLaSuite(false);
      })
      .catch((cause) => {
        if (!vivant.current) return;
        setErreur(messageDe(cause));
        setChargeLaSuite(false);
      });
  }, [appel, elements.length]);

  const recharger = useCallback(() => setTentative((n) => n + 1), []);
  return {
    elements,
    total,
    erreur,
    enCours,
    chargeLaSuite,
    reste: Math.max(0, total - elements.length),
    chargerLaSuite,
    recharger,
  };
}
